/**
 * Shared ids and enums.
 *
 * Convention: anything that lives inside SimState is a NUMBER (so cloning/hashing stay trivial), declared as a
 * `const` object plus a same-named type union. Anything that only crosses the network/UI boundary may be a string.
 * Never renumber existing values (replays and protocol compatibility); append new ones.
 */

import { deepFreeze } from './freeze';

// ── Specials ───────────────────────────────────────────────────────────────────────────────────────────
export const SpecialId = {
  NONE: 0,
  // Signature specials of the initial four characters (implemented first)
  HAIL_MARY: 1,
  BULLDOZER: 2,
  MIRAGE: 3,
  BLITZ: 4,
  // Pool from VISION §5 (future / "Standard" pool). Ids reserved so they never collide.
  BULLET_PASS: 5,
  ROCKET_BOOTS: 6,
  STICKY_GLOVES: 7,
  BIG_HEAD: 8,
  GROUND_POUND: 9,
  SHADOW: 10,
  PICK_SIX_HANDS: 11,
  LASER_ARM: 12,
  BANANA_PUNT: 13,
  MAGNET_ONSIDE: 14,
  // New roster wave (characters.ts): appended, never renumbered.
  DOUBLE_JUMP: 15,
} as const;
export type SpecialId = (typeof SpecialId)[keyof typeof SpecialId];

/** Which side may activate a special. */
export const SpecialSide = { OFFENSE: 0, DEFENSE: 1, ANY: 2, TOSS: 3 } as const;
export type SpecialSide = (typeof SpecialSide)[keyof typeof SpecialSide];

export interface SpecialInfo {
  readonly id: SpecialId;
  /** Stable string key (asset names, i18n, analytics). */
  readonly key: string;
  readonly name: string;
  readonly side: SpecialSide;
  /** One-line description for the move list / character select. */
  readonly blurb: string;
  /** Counter hint for the move list. */
  readonly counter: string;
  /** Implemented in the sim (false = reserved id only). */
  readonly implemented: boolean;
}

export const SPECIALS: readonly SpecialInfo[] = deepFreeze([
  {
    id: SpecialId.NONE,
    key: 'none',
    name: '—',
    side: SpecialSide.ANY,
    blurb: '',
    counter: '',
    implemented: true,
  },
  {
    id: SpecialId.HAIL_MARY,
    key: 'hail_mary',
    name: 'HAIL MARY',
    side: SpecialSide.OFFENSE,
    blurb: 'Your next throw is a huge glowing arc with 1.3× hang time. Throw it and chase it!',
    counter: 'Slow and obvious — get under it.',
    implemented: true,
  },
  {
    id: SpecialId.BULLDOZER,
    key: 'bulldozer',
    name: 'BULLDOZER',
    side: SpecialSide.OFFENSE,
    blurb: 'For 3 s your next grounded tackle is broken and the tackler is stunned.',
    counter: 'Airborne (Leap) tackles still work.',
    implemented: true,
  },
  {
    id: SpecialId.MIRAGE,
    key: 'mirage',
    name: 'MIRAGE',
    side: SpecialSide.OFFENSE,
    blurb: 'Spawns a decoy clone that runs the other way for 1 s.',
    counter: 'Only the real one casts a ball shadow.',
    implemented: true,
  },
  {
    id: SpecialId.BLITZ,
    key: 'blitz',
    name: 'BLITZ',
    side: SpecialSide.DEFENSE,
    blurb: 'Skip the rush count this play. The whistle reveals it at the snap.',
    counter: 'Quick throw or scramble.',
    implemented: true,
  },
  {
    id: SpecialId.BULLET_PASS,
    key: 'bullet_pass',
    name: 'BULLET PASS',
    side: SpecialSide.OFFENSE,
    blurb: 'Flat fast throw that cannot be swatted.',
    counter: 'Jump into its path.',
    implemented: false,
  },
  {
    id: SpecialId.ROCKET_BOOTS,
    key: 'rocket_boots',
    name: 'ROCKET BOOTS',
    side: SpecialSide.OFFENSE,
    blurb: 'A 2 s speed burst on your next run with the ball.',
    counter: 'Just ride it out — it fades.',
    implemented: true,
  },
  {
    id: SpecialId.STICKY_GLOVES,
    key: 'sticky_gloves',
    name: 'STICKY GLOVES',
    side: SpecialSide.OFFENSE,
    blurb: 'Bigger catch radius for one play.',
    counter: 'Knock it away first.',
    implemented: false,
  },
  {
    id: SpecialId.BIG_HEAD,
    key: 'big_head',
    name: 'BIG HEAD',
    side: SpecialSide.DEFENSE,
    blurb: 'Head inflates: bigger grab and swat range for 2.5 s.',
    counter: 'Slower while it lasts — run past.',
    implemented: true,
  },
  {
    id: SpecialId.GROUND_POUND,
    key: 'ground_pound',
    name: 'GROUND POUND',
    side: SpecialSide.DEFENSE,
    blurb: 'Shockwave stuns a nearby grounded carrier.',
    counter: 'Be airborne when it lands.',
    implemented: true,
  },
  {
    id: SpecialId.SHADOW,
    key: 'shadow',
    name: 'SHADOW',
    side: SpecialSide.DEFENSE,
    blurb: "Mirror the carrier's ground movement for 1.5 s.",
    counter: 'Fake Jump.',
    implemented: false,
  },
  {
    id: SpecialId.PICK_SIX_HANDS,
    key: 'pick_six_hands',
    name: 'PICK-SIX HANDS',
    side: SpecialSide.DEFENSE,
    blurb: 'Next touch on the ball is a guaranteed catch.',
    counter: "Don't throw near them.",
    implemented: false,
  },
  {
    id: SpecialId.LASER_ARM,
    key: 'laser_arm',
    name: 'LASER ARM',
    side: SpecialSide.TOSS,
    blurb: 'Field goal toss flies dead straight.',
    counter: '—',
    implemented: false,
  },
  {
    id: SpecialId.BANANA_PUNT,
    key: 'banana_punt',
    name: 'BANANA PUNT',
    side: SpecialSide.TOSS,
    blurb: 'Punt toss curves along a visible path.',
    counter: '—',
    implemented: false,
  },
  {
    id: SpecialId.MAGNET_ONSIDE,
    key: 'magnet',
    name: 'MAGNET',
    side: SpecialSide.ANY,
    blurb: 'Loose and airborne balls curve toward your glove.',
    counter: 'Keep it out of his radius.',
    implemented: true,
  },
  {
    id: SpecialId.DOUBLE_JUMP,
    key: 'double_jump',
    name: 'DOUBLE JUMP',
    side: SpecialSide.ANY,
    blurb: 'An extra jump in the air, whenever you need it this play.',
    counter: 'Time your lunge for the second hop.',
    implemented: true,
  },
]);

export const specialInfo = (id: SpecialId): SpecialInfo => SPECIALS[id] ?? SPECIALS[0]!;

/**
 * Specials setting of a match (VISION §5).
 *   OFF       no specials (the RANKED default)
 *   SIGNATURE each head's signature special (casual / vs CPU / private rooms)
 *   STANDARD  the shared "Standard" pool, VISION's ranked toggle. RESERVED: until the pool exists the sim treats
 *             it as OFF, and the server never sends it.
 */
export const SpecialsMode = { OFF: 0, SIGNATURE: 1, STANDARD: 2 } as const;
export type SpecialsMode = (typeof SpecialsMode)[keyof typeof SpecialsMode];

// ── Match format ───────────────────────────────────────────────────────────────────────────────────────
export const MatchFormat = {
  /** Default and ranked: N alternating possessions each from the opponent's 40, sudden-death OT pairs. */
  DRIVES: 0,
  /** Casual: 2 halves × 2:00 with kickoff/punt/onside tosses (later). */
  CLASSIC: 1,
} as const;
export type MatchFormat = (typeof MatchFormat)[keyof typeof MatchFormat];

// ── CPU difficulty ─────────────────────────────────────────────────────────────────────────────────────
export const Difficulty = { EASY: 0, NORMAL: 1, HARD: 2, ALL_PRO: 3 } as const;
export type Difficulty = (typeof Difficulty)[keyof typeof Difficulty];
export const DIFFICULTY_NAMES: Readonly<Record<Difficulty, string>> = {
  0: 'EASY',
  1: 'NORMAL',
  2: 'HARD',
  3: 'ALL-PRO',
};

// ── Game modes (client-level, strings) ────────────────────────────────────────────────────────────────
export const GAME_MODES = [
  'cpu',
  'local',
  'online_quick',
  'online_ranked',
  'private',
  'tutorial',
  'practice',
] as const;
export type GameMode = (typeof GAME_MODES)[number];

/** Player slot. 0 = HOME (red), 1 = AWAY (blue). */
export type PlayerIndex = 0 | 1;
export const otherPlayer = (p: PlayerIndex): PlayerIndex => (p === 0 ? 1 : 0);
export const TEAM_COLORS = deepFreeze({
  home: { main: '#e8343c', dark: '#a8161f' },
  away: { main: '#2f7df6', dark: '#1647a8' },
} as const);
